import { useSkeem } from "../providers/SkeemProvider"
import { IRecord } from "../types/schema"
import { batch } from "./array"
import { IEventDetailsFor, IEventModel, IEventType } from "./eventUtils"

export type FormattedEventRecord = {
  type: string
  model: string
  recordId: string
  details: any
  user: any
}

export function useEvents() {
  const skeem = useSkeem()

  async function saveEvents(events: FormattedEventRecord[]) {
    if (events.length === 0) return Promise.resolve()

    let records: any[] = []
    await batch({
      arr: events,
      size: 250,
      async fn(batch) {
        const r = await skeem.mutate({ events: batch.map(event => ({ create: event })) })
        records = records.concat(r)
      },
    })
    return records
  }

  function buildRaw<T extends IEventType>(
    type: T,
    model: IEventModel,
    recordId: string,
    details: IEventDetailsFor<T>,
  ): FormattedEventRecord {
    return {
      type,
      model,
      recordId,
      details,
      user: { set: { session: true } },
    }
  }

  const build = {
    message(model: IEventModel, recordId: string, message: string): FormattedEventRecord {
      return buildRaw("message", model, recordId, { message })
    },
  }

  async function createRaw<T extends IEventType>(
    type: T,
    model: IEventModel,
    recordId: string,
    details: IEventDetailsFor<T>,
  ): Promise<IRecord<"events">> {
    return createRawObj({ type, model, recordId, data: details })
  }

  async function createRawObj<T extends IEventType>(obj: {
    type: T
    model: IEventModel
    recordId: string
    date?: Date | null
    timeTakenMinutes?: number | null
    data: IEventDetailsFor<T>
  }): Promise<IRecord<"events">> {
    return skeem.mutate({
      events: {
        create: {
          type: obj.type,
          model: obj.model,
          recordId: obj.recordId,
          details: obj.data,
          user: skeem.currentSession.id ? { set: { session: true } } : undefined,
          date: obj.date || undefined,
          timeTakenMinutes: obj.timeTakenMinutes || undefined,
        },
      },
    })
  }

  function createRecord(model: IEventModel, recordId: string) {
    return createRaw("create", model, recordId, undefined)
  }
  function updateRecord(model: IEventModel, recordId: string, details: IEventDetailsFor<"update">) {
    return createRaw("update", model, recordId, details)
  }
  function createNote(
    model: IEventModel,
    recordId: string,
    message: string,
    eventDetails?: {
      stepData?: any
      date?: Date | null
      timeTakenMinutes?: number | null
      linkHref?: string | null
      linkText?: string | null
      noteType?: string
    },
  ) {
    return createRawObj({
      type: "note",
      model,
      recordId,
      date: eventDetails?.date,
      timeTakenMinutes: eventDetails?.timeTakenMinutes,
      data: {
        message: message.trim(),
        noteType: eventDetails?.noteType,
        eventDetails: {
          stepData: eventDetails?.stepData,
          linkHref: eventDetails?.linkHref || undefined,
          linkText: eventDetails?.linkText || undefined,
        },
      },
    })
  }
  function createEmailMessage(
    model: IEventModel,
    recordId: string,
    assessorEmail: string,
    message: string,
    eventDetails?: any,
  ) {
    return createRaw("emailMessage", model, recordId, { message, assessorEmail, eventDetails })
  }
  function createLoggedIn(recordId: string) {
    return createRaw("loggedIn", "skeem_users", recordId, undefined)
  }
  function createLetter(
    recordId: string,
    date: Date,
    notes: string | undefined,
    timeTakenMinutes?: number | null,
  ) {
    return createRawObj({
      type: "letter",
      model: "results",
      recordId,
      date,
      data: { date, notes },
      timeTakenMinutes,
    })
  }
  function createMeeting(
    recordId: string,
    date: Date,
    notes: string | undefined,
    timeTakenMinutes?: number | null,
  ) {
    return createRawObj({
      type: "meeting",
      model: "results",
      recordId,
      date,
      timeTakenMinutes,
      data: { date, notes },
    })
  }
  function createReminder(recordId: string, reminderId: string, date: Date, body: string) {
    return createRaw("reminder", "results", recordId, { reminderId, date, body })
  }
  function createActionedStep(recordId: string, name: string, value: boolean) {
    return createRaw("actionedStep", "results", recordId, { name, value })
  }
  function createCaseStateChanged(recordId: string, closed: boolean, status: string) {
    return createRaw("caseStateChange", "results", recordId, { closed, status })
  }
  function createFiles(
    recordId: string,
    fileboyId: string,
    extraData?: {
      flowFileId?: string
      date?: Date | null
      timeTakenMinutes?: number | null
      notes?: string
    },
  ) {
    return createRawObj({
      type: "file",
      model: "results",
      recordId,
      date: extraData?.date || undefined,
      timeTakenMinutes: extraData?.timeTakenMinutes,
      data: { fileboyId, flowFileId: extraData?.flowFileId, notes: extraData?.notes },
    })
  }
  function createImport(recordId: string, data: { type: string; data: Record<string, any> }) {
    return createRaw("import", "companies", recordId, data)
  }
  function createMessage(model: IEventModel, recordId: string, message: string) {
    return createRaw("message", model, recordId, { message })
  }

  function createCompletedQuestionSet(opts: { resultId: string; stepId: string }) {
    return createRaw("completedQuestionSet", "results", opts.resultId, {
      stepId: opts.stepId,
    } as any)
  }

  async function createDownloadedLetter(opts: {
    userId: string
    fileId: string
    resultsId?: string
    fileName: string
    fileType: string
    date: Date
    requiresApproval?: boolean
  }) {
    await createRaw("downloadedLetter", "skeem_users", opts.userId, {
      fileId: opts.fileId,
      fileName: opts.fileName,
      fileType: opts.fileType,
      date: opts.date,
      requiresApproval: !opts.requiresApproval,
    })
    if (opts.resultsId) {
      await createRaw("downloadedLetter", "results", opts.resultsId, {
        fileId: opts.fileId,
        fileName: opts.fileName,
        fileType: opts.fileType,
        date: opts.date,
        requiresApproval: !opts.requiresApproval,
      })
    }
  }

  async function createFlowResultFileUpload(opts: {
    flowResultFileId: string
    date: Date
    userId: string
    resultId: string
    fileboyId: string
    fileName: string
  }) {
    await createRaw("flowResultFileUpload", "results", opts.resultId, {
      flowResultFileId: opts.flowResultFileId,
      date: opts.date,
      fileName: opts.fileName,
      fileboyId: opts.fileboyId,
      userId: opts.userId,
    })
  }
  async function createFlowResultFileApproved(opts: {
    flowResultFileId: string
    date: Date
    userId: string
    resultId: string
    fileName: string
  }) {
    await createRaw("flowResultFileApproved", "results", opts.resultId, {
      flowResultFileId: opts.flowResultFileId,
      date: opts.date,
      fileName: opts.fileName,
    })
  }
  async function createFlowResultFileDenied(opts: {
    flowResultFileId: string
    date: Date
    userId: string
    resultId: string
    fileName: string
  }) {
    await createRaw("flowResultFileDenied", "results", opts.resultId, {
      flowResultFileId: opts.flowResultFileId,
      date: opts.date,
      fileName: opts.fileName,
    })
  }

  async function createFlowResultFileRequestedChanges(opts: {
    flowResultFileId: string
    date: Date
    userId: string
    resultId: string
    fileName: string
  }) {
    await createRaw("flowResultFileRequestedChanges", "results", opts.resultId, {
      flowResultFileId: opts.flowResultFileId,
      date: opts.date,
      fileName: opts.fileName,
    })
  }

  async function createFlowResultFileRequest(opts: {
    flowResultFileId: string
    date: Date
    userId: string
    resultId: string
    /** Snapshot of who could approve at send time (support users on the file). */
    approverNames?: string[]
  }) {
    await createRaw("flowResultFileRequest", "results", opts.resultId, {
      flowResultFileId: opts.flowResultFileId,
      date: opts.date,
      ...(opts.approverNames?.length ? { approverNames: opts.approverNames } : {}),
    })
  }
  async function createFlowResultFileRequestApproval(opts: {
    flowResultFileId: string
    date: Date
    userId: string
    resultId: string
    fileName: string
  }) {
    await createRaw("flowResultFileRequestApproval", "results", opts.resultId, {
      flowResultFileId: opts.flowResultFileId,
      date: opts.date,
      fileName: opts.fileName,
    })
  }

  function createAssociatedRecord(
    model: IEventModel,
    recordId: string,
    details: IEventDetailsFor<"createAssociation">,
  ) {
    return createRaw("createAssociation", model, recordId, details)
  }
  function createEmployeeCompetencyAward(
    recordId: string,
    date: Date,
    notes: string | undefined,
    competency: string,
  ) {
    return createRaw("employeeCompetencyAward", "employees", recordId, { date, notes, competency })
  }
  function createScheduledAppraisal(recordId: string, date: Date, id: string) {
    return createRaw("scheduledAppraisal", "employees", recordId, {
      date,
      id,
    })
  }
  function createCompletedAppraisal(recordId: string, date: Date, id: string) {
    return createRaw("completedAppraisal", "employees", recordId, {
      date,
      id,
    })
  }

  return {
    createRecord,
    updateRecord,
    createAssociatedRecord,
    createNote,
    createEmailMessage,
    createLoggedIn,
    createLetter,
    createMeeting,
    createReminder,
    createActionedStep,
    createCaseStateChanged,
    createDownloadedLetter,
    createFiles,
    createImport,
    createEmployeeCompetencyAward,
    createScheduledAppraisal,
    createCompletedAppraisal,
    createFlowResultFileUpload,
    createFlowResultFileRequest,
    createFlowResultFileRequestApproval,
    createFlowResultFileApproved,
    createFlowResultFileDenied,
    createFlowResultFileRequestedChanges,
    createCompletedQuestionSet,
    message: createMessage,
    build,
    saveEvents,
  }
}
